import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { isValid } from 'redux-form'
import { push } from 'connected-react-router'
import {
  FormattedHTMLMessage,
  FormattedMessage,
  FormattedNumber,
  FormattedDate,
  injectIntl,
} from 'react-intl'
import { FormattedDuration } from 'shared/units'
import { LONG } from 'shared/units/constants'
import * as premiumActions from 'services/premium/actions'
import { fetchGeoIp } from 'services/auth/actions'
import Button from 'components/util/Button'
import Step from './Step'
import LineItem from './LineItem'
import ProductChooser from './ProductChooser'
import CCForm from './CCForm/CCForm'
import styles from './_checkoutPage.css'
import Svg from 'components/util/Svg'
import premiumStyles from './_premium.css'
import * as bootupSelectors from 'services/bootup/selectors'
import * as site from 'services/site/selectors'
import * as user from 'services/user/selectors'
import * as premium from 'services/premium/selectors'
import * as auth from 'services/auth/selectors'
import { selectLocation } from 'services/routing/selectors'
import Modal from 'components/util/Modal'
import Loader from 'components/util/Loader'
import BillingProfile from './BillingProfile'
import sharedMessages from './messages'

const mapStateToProps = (state) => ({
  accountPortalRoot: bootupSelectors.selectAccountPortalRoot(state),
  customerSupportLink: bootupSelectors.selectCustomerSupportLink(state),
  siteName: site.selectSiteName(state),
  userId: user.selectUserId(state),
  displayName: user.selectDisplayName(state),
  errorDescription: premium.selectErrorDescription(state),
  error: premium.selectError(state),
  errorCode: premium.selectErrorCode(state),
  submitting: premium.selectSubmitting(state),
  submittingAction: premium.selectSubmittingAction(state),
  success: premium.selectSuccess(state),
  products: premium.selectProducts(state),
  couponCode: premium.selectCouponCode(state),
  product: premium.selectProduct(state),
  productInterval: premium.selectProductInterval(state),
  productUnit: premium.selectProductUnit(state),
  productEndDate: premium.selectProductEndDate(state),
  isTrial: premium.selectIsTrial(state),
  isPromo: premium.selectIsPromo(state),
  currency: premium.selectCurrency(state),
  paymentRequired: premium.selectPaymentRequired(state),
  checkoutPageLoading: premium.selectCheckoutPageLoading(state),
  subscriptions: premium.selectSubscriptions(state),
  subscription: premium.selectSubscription(state),
  productAmount: premium.selectProductAmount(state),
  productFreeEndDate: premium.selectProductFreeEndDate(state),
  productFreeInterval: premium.selectProductFreeInterval(state),
  productFreeUnit: premium.selectProductFreeUnit(state),
  loadError: premium.selectLoadError(state),
  billingProfiles: premium.selectBillingProfiles(state),
  defaultBillingProfile: premium.selectDefaultBillingProfile(state),
  billingProfileSuccess: premium.selectBillingProfileSuccess(state),
  ccFormIsValid: isValid('creditCard')(state),
  countryCode: auth.selectAuthCountry(state),
  location: selectLocation(state),
})

const mapDispatchToActions = {
  ...premiumActions,
  push,
  fetchGeoIp,
}

export class CheckoutPage extends Component {

  static propTypes = {
    accountPortalRoot: PropTypes.string,
    customerSupportLink: PropTypes.string,
    siteName: PropTypes.string,
    submitting: PropTypes.bool,
    submittingAction: PropTypes.string,
    error: PropTypes.bool,
    errorCode: PropTypes.string,
    errorDescription: PropTypes.string,
    success: PropTypes.bool,
    push: PropTypes.func,
    fetchProducts: PropTypes.func,
    products: PropTypes.array,
    applyCoupon: PropTypes.func,
    userId: PropTypes.number,
    location: PropTypes.object,
    product: PropTypes.object,
    couponCode: PropTypes.string,
    fetchBillingProfiles: PropTypes.func,
    billingProfiles: PropTypes.array,
    productInterval: PropTypes.number,
    productUnit: PropTypes.string,
    productEndDate: PropTypes.object,
    chooseProduct: PropTypes.func,
    isPromo: PropTypes.bool,
    isTrial: PropTypes.bool,
    currency: PropTypes.string,
    paymentRequired: PropTypes.bool,
    chooserProducts: PropTypes.array,
    displayName: PropTypes.string,
    checkout: PropTypes.func,
    checkoutPageLoading: PropTypes.bool,
    loadCheckoutPage: PropTypes.func,
    subscriptions: PropTypes.array,
    legacyQueryProducts: PropTypes.object,
    subscription: PropTypes.object,
    productAmount: PropTypes.number,
    productFreeEndDate: PropTypes.object,
    productFreeInterval: PropTypes.number,
    productFreeUnit: PropTypes.string,
    intl: PropTypes.object,
    loadError: PropTypes.bool,
    defaultBillingProfile: PropTypes.object,
    updateDefaultBillingProfile: PropTypes.func,
    billingProfileSuccess: PropTypes.bool,
    deleteBillingProfile: PropTypes.func,
    updateBillingProfile: PropTypes.func,
    ccFormIsValid: PropTypes.bool,
    fetchGeoIp: PropTypes.func,
    countryCode: PropTypes.string,
  };

  static defaultProps = {
    chooserProducts: [
      'c74e1db088223cb4fcaad209d9a0fb73',
      '53cac6ce7ba0bd89a35f3eae9821b9af',
    ],
    legacyQueryProducts: {
      'mvp1': 'c74e1db088223cb4fcaad209d9a0fb73',
      'mvp12': '53cac6ce7ba0bd89a35f3eae9821b9af',
    },
  }

  state = {
    paymentRequired: null,
    isSelectingCard: false,
    isUsingNewCard: false,
  }

  componentWillMount () {
    const { location: { query: { code, discount, discount_flag }}} = this.props
    const { userId, chooserProducts } = this.props
    const couponCode = code || discount

    if (discount_flag && discount_flag === '1') { // eslint-disable-line camelcase
      this.props.push('/premium/coupon')
    }

    Promise.resolve().then(() => {
      return this.props.fetchGeoIp()
    }).then(() => {
      return this.props.loadCheckoutPage({ code: couponCode, userId, chooserProducts })
        .then(() => {
          const { isPromo, subscription: activeSubscription, paymentRequired } = this.props
          if (activeSubscription && !isPromo) {
            this.props.push('/premium/membership')
          } else {
            this.setState({ paymentRequired })
          }
        })
        .catch(this.handleCouponError) // redirect if coupon error, otherwise error will show on page
    })
  }

  componentWillReceiveProps (nextProps) {
    this.handleCouponError(nextProps)

    if (this.props.billingProfileSuccess === null && nextProps.billingProfileSuccess === true) {
      this.setState({ isUsingNewCard: false })
    }
  }

  /**
   * Inspect the current state to determine if the error is related to a coupon.
   * If it is, redirect to the coupon page, which has better error messages
   * and allows the user to type in the coupon code directly to confirm and/or fix
   * typos.
   *
   * @param  {Object} props - props object to use, default to this.props
   */
  handleCouponError = (props = this.props) => {
    const { error, errorCode } = props
    if (error && errorCode && errorCode.match('coupon')) {
      this.props.push('/premium/coupon')
    }
  }

  /**
   * Determines which product the productChooser should default to based on query params.
   * Written to support legacy query params that are supported in today's checkout.
   *
   * given: ?product=c74e1db088223cb4fcaad209d9a0fb73
   *   => chooses that product directly
   *   => index: 0
   * given: ?product=mvp12
   *   => will choose based on lookup in this.props.legacyQueryProducts
   *   => index: 1
   * given: ?product=mvp&subscription_length=12
   *   => will concatenate product and subscription_length ('mvp12')
   *   => will choose based on lookup in this.props.legacyQueryProducts
   *   => index: 1
   *
   * @return {Number}
   */
  getChosenProductIndex () {
    const { location: { query: { subscription_length: subLength, product }}} = this.props
    const { legacyQueryProducts, chooserProducts } = this.props
    const queryId = subLength ? `${product}${subLength}` : product
    const legacyProduct = queryId in legacyQueryProducts
    const chosenId = legacyProduct ? legacyQueryProducts[queryId] : queryId
    const index = chooserProducts.findIndex((id) => id === chosenId)
    return (index < 0) ? 0 : index
  }

  handleContinue = () => {
    const { billingProfiles, paymentRequired, isPromo } = this.props
    const { isUsingNewCard, isEditingCard } = this.state

    if (isEditingCard) { return }

    this.setState({ error: null })

    if (paymentRequired && this._ccForm && (!billingProfiles.length || isUsingNewCard)) {
      this._ccForm.getWrappedInstance().submit()
    } else if (isPromo) {
      this.checkoutWithPromo()
    } else {
      this.checkoutWithExisting()
    }
  }

  handleCCFormSubmit = (card) => {
    this.checkoutWithCard(card)
  }

  handleEditSubmit = (id) => (card) => {
    this.props.updateBillingProfile(id, card)
      .then(() => this.setState({ isEditingCard: false }))
      .catch(() => this.setState({ isEditingCard: false }))
  }

  handleToggleNew = () => {
    this.setState({ isUsingNewCard: !this.state.isUsingNewCard })
  }

  handleCheckoutSuccess = () => {
    this.props.push(`/premium/receipt/${this.props.subscription.subscription_id}`)
  }

  handleSelectExisting = (profile) => {
    this.setState({ existingProfile: profile, isSelectingCard: false })
  }

  handleDeleteBillingProfile = (profile) => {
    this.props.deleteBillingProfile(profile.billing_profile_id)
  }

  handleToggleExisting = () => {
    this.setState({ isSelectingCard: !this.state.isSelectingCard })
  }

  handleEditBillingProfile = (profile) => {
    this.setState({ isEditingCard: true, profileToEdit: profile })
  }

  handleCancelEditBillingProfile = () => {
    this.setState({ isEditingCard: false })
  }

  checkoutWithCard (card) {
    const { couponCode, product } = this.props
    return this.checkout({ couponCode, product, provider: 'stripe', card })
  }

  checkoutWithExisting () {
    const { existingProfile } = this.state
    const { couponCode, product, defaultBillingProfile } = this.props
    const defaultId = defaultBillingProfile.billing_profile_id
    const didSelectExisting = Boolean(existingProfile)
    const newDefaultProfile = didSelectExisting && (defaultId !== existingProfile.billing_profile_id)
    const billingProfileId = newDefaultProfile ? existingProfile.billing_profile_id : null
    return this.checkout({ product, couponCode, billingProfileId, provider: 'stripe' })
  }

  checkoutWithPromo () {
    const { couponCode, product } = this.props
    return this.checkout({ couponCode, product, provider: 'myfitnesspal' })
  }

  checkout ({ card, product, couponCode, provider, billingProfileId }) {
    const { countryCode } = this.props
    this.props.checkout({ card, product, coupon: couponCode, provider, billingProfileId, countryCode })
      .then(this.handleCheckoutSuccess)
      .catch(this.handleCheckoutError)
  }

  getCheckoutError = () => {
    const { errorCode, errorDescription } = this.props
    return sharedMessages.errors[errorCode] || errorDescription
  }

  shouldProfileBeChecked = (profile) => {
    const { existingProfile } = this.state
    if (!existingProfile) { return profile.default }
    return profile.billing_profile_id === existingProfile.billing_profile_id
  }

  render () {
    const {
      accountPortalRoot,
      customerSupportLink,
      error,
      products,
      siteName,
      submitting,
      couponCode,
      product,
      productUnit,
      isPromo,
      isTrial,
      currency,
      displayName,
      checkoutPageLoading,
      chooseProduct: handleChooseProduct,
      productInterval,
      productEndDate,
      productFreeEndDate,
      productAmount,
      productFreeInterval,
      productFreeUnit,
      intl,
      loadError,
      billingProfiles,
      defaultBillingProfile,
      submittingAction,
      ccFormIsValid,
    } = this.props

    const {
      existingProfile,
      paymentRequired,
      isUsingNewCard,
      isSelectingCard,
      isEditingCard,
      profileToEdit,
    } = this.state

    return (
      <section className={styles.container}>
        <Helmet title={intl.formatMessage({
          id: 'premium.checkout.title',
          defaultMessage: 'MVP Checkout',
        })} />

        <Modal isOpen={submitting && !checkoutPageLoading}>
          <div className={premiumStyles.loadingModal}>
            <div>
              <Loader height={50} width={50} />
            </div>
            <h3 className={premiumStyles.loadingTitle}>
              {sharedMessages.submittingActions[submittingAction] ||
                <FormattedMessage
                  id='premium.checkout.submitting'
                  defaultMessage='Processing...'
                />
              }
            </h3>
          </div>
        </Modal>

        {checkoutPageLoading ?
          <div className={styles.pageLoader}>
            <Loader type='pulse' height={60} width={60} style={{ marginTop: 200 }} />
          </div>
        : null}

        <h2 className={styles.title}>
          {isPromo ?
            <FormattedMessage
              id='premium.checkout.headline_promo'
              defaultMessage='{siteName} MVP!'
              values={{ siteName: siteName }}
            />
          :
            <FormattedMessage
              id='premium.checkout.headline'
              defaultMessage='Upgrade to {siteName} MVP!'
              values={{ siteName: siteName }}
            />
          }
        </h2>

        <div className={styles.loggedIn}>
          <FormattedMessage
            id='premium.checkout.logged_in_as'
            defaultMessage='Logged in as {displayName}'
            values={{ displayName: <b className={styles.displayName}>{displayName}</b> }}
          />
          <a className={styles.link} href='/auth/logout'>
            <FormattedMessage id='premium.checkout.not_you' defaultMessage='Not you?' />
          </a>
        </div>

        {couponCode ?

          <Step number={isPromo ? null : 1} title='Subscription Details'>

            {/* coupon details */}
            <LineItem>
              <div>
                {isPromo ?
                  <FormattedMessage
                    id='premium.checkout.discount_code'
                    defaultMessage='Promo code: {discountCode}'
                    values={{ discountCode: couponCode }}
                  />
                :
                  <FormattedMessage
                    id='premium.checkout.promo_code'
                    defaultMessage='Discount code: {discountCode}'
                    values={{ discountCode: couponCode }}
                  />
                }
              </div>

              <div className={styles.credit}>
                <FormattedMessage
                  id='premium.checkout.product_free_duration'
                  defaultMessage='{freeDuration} free'
                  values={{
                    freeDuration: (
                      <FormattedDuration
                        value={productFreeInterval}
                        from={productFreeUnit}
                        digits={0}
                        unit={LONG}
                      />
                    ),
                  }}
                />
              </div>
            </LineItem>

            {product ?
              <LineItem>
                <FormattedMessage
                  id='premium.checkout.plan_details'
                  defaultMessage='Plan: {freeDuration} MVP Subscription, ends on {endDate}'
                  values={{
                    freeDuration: (
                      <FormattedDuration
                        value={(isPromo || isTrial) ? productFreeInterval : productInterval}
                        from={(isPromo || isTrial) ? productFreeUnit : productUnit}
                        digits={0}
                        unit={LONG}
                      />
                    ),
                    endDate: <FormattedDate value={productEndDate} />,
                  }}
                />
              </LineItem>
            : null}

            {/* selected product details */}
            {product && isTrial ?
              <LineItem>
                <FormattedMessage
                  id='premium.checkout.initial_cost'
                  defaultMessage='Initial cost'
                />
                <FormattedNumber
                  style='currency'
                  currencyDisplay='symbol'
                  value={0}
                  currency={currency}
                />
              </LineItem>
            : null}

          </Step>
        :
          (products && products.length ?
            <Step number={1} title='Select a Product'>
              <ProductChooser
                products={products}
                defaultIndex={this.getChosenProductIndex()}
                onSelect={handleChooseProduct}
              />
            </Step>
          : null)
        }

        {/* credit card fieldset */}
        {paymentRequired ?
          <Step number={2} title='Card Information'>

            {billingProfiles.length && !isEditingCard && !isSelectingCard ?
              <div className={styles.cardMetaButton}>
                <Button
                  el='button'
                  size='small'
                  icon={isUsingNewCard ? 'x' : 'add'}
                  type='secondary'
                  onClick={this.handleToggleNew}
                >
                  {isUsingNewCard
                    ? <FormattedMessage id='premium.checkout.new_card' defaultMessage='Cancel new card' />
                    : <FormattedMessage id='premium.checkout.cancel_new_card' defaultMessage='New card' />
                  }
                </Button>
              </div>
            : null}

            {isSelectingCard ?
              <div className={styles.cardMetaButton}>
                <Button
                  el='button'
                  size='small'
                  icon='x'
                  type='secondary'
                  onClick={this.handleToggleExisting}
                >
                  <FormattedMessage id='premium.checkout.cancel' defaultMessage='Cancel' />
                </Button>
              </div>
            : null}

            {defaultBillingProfile && !isEditingCard && !isSelectingCard && !isUsingNewCard ?
              <BillingProfile
                key='default'
                profile={existingProfile || defaultBillingProfile}
                lineItem={false}
                allowSelect={false}
                allowDelete={false}
                allowEdit
                className={styles.defaultBillingProfile}
                onEdit={this.handleEditBillingProfile}
              />
            : null}

            {isSelectingCard && !isUsingNewCard ? billingProfiles.map((profile, i) => (
              <BillingProfile
                index={i}
                key={profile.billing_profile_id}
                profile={profile}
                onSelect={this.handleSelectExisting}
                checked={this.shouldProfileBeChecked(profile)}
                onDelete={this.handleDeleteBillingProfile}
              />
            )) : null}

            {billingProfiles.length > 1 && !isEditingCard && !isSelectingCard && !isUsingNewCard ?
              <div className={styles.cardSelectToggle} onClick={this.handleToggleExisting}>
                <span className={styles.cardSelectToggleText}>
                  <FormattedMessage
                    id='premium.checkout.select_existing_card'
                    defaultMessage='Select a different credit/debit card'
                  />
                  <Svg name='keyboard_arrow_down' className={styles.cardSelectToggleIcon} />
                </span>
              </div>
            : null}

            {!defaultBillingProfile || isUsingNewCard ?
              <CCForm
                mode='create'
                ref={(c) => { this._ccForm = c }}
                onSubmit={this.handleCCFormSubmit}
                showButtons={false}
              />
            : null}

            {isEditingCard ?
              <CCForm
                mode='edit'
                style={{ marginRight: 0 }}
                ref={(c) => { this._ccForm = c }}
                defaultProfile={profileToEdit}
                onCancel={this.handleCancelEditBillingProfile}
                onSubmit={this.handleEditSubmit(profileToEdit.billing_profile_id)}
              />
            : null}

            {product && isTrial ?
              <div className={styles.note}>
                <FormattedMessage
                  id='premium.checkout.trial.note'
                  defaultMessage={`
                    You will not be charged during your ({freeDuration}) free trial. Your
                    subscription will renew at {displayPrice} per {productUnit} on {renewDate}
                  `}
                  values={{
                    displayPrice: (
                      <FormattedNumber
                        style='currency'
                        currencyDisplay='symbol'
                        value={productAmount}
                        currency={currency}
                      />
                    ),
                    renewDate: <FormattedDate value={productFreeEndDate} />,
                    freeDuration: (
                      <FormattedDuration
                        value={productFreeInterval}
                        from={productFreeUnit}
                        digits={0}
                        unit={LONG}
                      />
                    ),
                    productUnit,
                  }}
                />
              </div>
            : null}

          </Step>
        : null}

        {/* total */}
        {!loadError ?
          <LineItem bold>
            <div>
              <FormattedMessage
                id='premium.checkout.total_cost'
                defaultMessage='Total cost'
              />
            </div>
            <div>
              {product ?
                <FormattedNumber
                  style='currency'
                  currencyDisplay='symbol'
                  value={isPromo || isTrial ? 0 : productAmount}
                  currency={currency}
                />
              : null}
            </div>
          </LineItem>
        : null}

        {/* errors */}
        {error ?
          <div className={styles.errorMessage}>{this.getCheckoutError()}</div>
        : null}

        {/* submit */}
        {!loadError ?
          <div className={styles.submit}>
            <Button
              el='button'
              size='large'
              onClick={this.handleContinue}
              loading={submitting}
              disabled={submitting || isEditingCard || !ccFormIsValid}
              className={styles.submitButton}
            >
              {isPromo
                ? <FormattedMessage id='premium.checkout.redeem' defaultMessage='Redeem' />
                : <FormattedMessage id='premium.checkout.subscribe' defaultMessage='Subscribe' />
              }
            </Button>
          </div>
        : null}

        {/* legal, terms, etc... */}
        <footer className={styles.legal}>
          {!loadError ?
            <p>
              <FormattedHTMLMessage
                id='premium.checkout.legal.terms'
                defaultMessage={`
                  By continuing, I confirm that I have read and agreed to the
                  specific terms and conditions applicable to
                  <a href="{termsLink}" rel="noopener noreferrer" target="_blank">Paid Services</a>
                  set out in the Terms and Conditions of Use.
                `}
                values={{
                  termsLink: `${accountPortalRoot}/terms-and-conditions#8-paid-services`,
                }}
              />
            </p>
          : null}

          {!isPromo && !loadError ?
            <p>
              <FormattedHTMLMessage
                id='premium.checkout.legal.auto_renewal'
                defaultMessage={`
                  Once your payment is processed you will be able to access this item electronically.
                  We do not offer refunds or exchanges. Read the applicable
                  <a href="{termsLink}" rel="noopener noreferrer" target="_blank">terms and conditions</a>
                `}
                values={{
                  termsLink: `${accountPortalRoot}/terms-and-conditions#8-paid-services`,
                }}
              />
            </p>
          : null}

          <p>
            <FormattedHTMLMessage
              id='premium.checkout.legal.problems'
              defaultMessage={`
                If you experience any problems, please contact
                <a href="{supportLink}" rel="noopener noreferrer" target="_blank">
                  customer support.
                </a>
              `}
              values={{
                supportLink: customerSupportLink,
              }}
            />
          </p>
        </footer>

      </section>
    )
  }

}

export const Page = injectIntl(CheckoutPage)
export default connect(mapStateToProps, mapDispatchToActions)(Page)
